import React, { memo } from 'react'
import Skeleton from '@jimmyboys/liita-ui/dist/components/Skeleton'

import styles from './CommentThreadCard.module.sass'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import Avatar from '@jimmyboys/liita-ui/dist/components/Avatar'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'

import Icon from '@jimmyboys/liita-ui/dist/components/Icon'
import ModalOptions from '@jimmyboys/liita-ui/dist/components/ModalOptions'
import FuseHighlight from 'components/FuseHighlight/FuseHighlight'
import Tooltip from '@jimmyboys/liita-ui/dist/components/Tooltip'
import InlineCommentReply from './InlineCommentReply'
import RecipientsNotified from 'context/Drawer/drawers/CommentThread/RecipientsNotified'

const OpenFileModal = ({ title, options, openFile }) => {
	return (
		<ModalOptions
			title={title}
			options={options}
			centerArrow
			fixedHeight='auto'
			alwaysAbove
			selectable={false}
			onSelect={(e) => {
				openFile && openFile(e)
				return true
			}}
		>
			<button className={styles['attachments']}>
				<Icon width='20px' height='20px' name='attachment' />
			</button>
		</ModalOptions>
	)
}

export const CommentAuthor = ({ author }) => {
	return (
		<span className={styles['avatar-container']}>
			<Tooltip text={author?.name || ''}>
				<div>
					<Avatar
						rounded
						className={styles['avatar']}
						user={author}
					/>
				</div>
			</Tooltip>
		</span>
	)
}

const CommentThreadCard = memo(
	(props) => {
		const {
			className,
			data,
			expanded,
			onToggleExpand,
			replies,
			...attributes
		} = props
		const { i18n, openFile } = useGlobal()
		const labels = data.$cardLabels || {}
		const hasReplies = (data.repliesCount ?? 0) > 0

		return (
			<div className={styles['thread-wrapper']}>
				<div
					{...attributes}
					className={`card ${styles['root'] || ''} ${
						className || ''
					}`}
				>
					<VStack className={styles['comment-type-date']}>
						<h5 className={styles['comment-type']}>
							{data?.providerEstablishment?.name ?? data.title}
						</h5>
						<Tooltip
							displayDelay={600}
							alwaysBelow
							content={
								<RecipientsNotified
									notifiedEntities={data.notifiedEntities}
									notifiedEstablishments={
										data.notifiedEstablishments
									}
								/>
							}
						>
							<small className={styles['comment-date']}>
								<Icon
									name='calendar'
									width='14px'
									height='14px'
								/>
								{data.date?.dateTimeFormat}
							</small>
						</Tooltip>
					</VStack>

					<div dynamic='true' className={styles['comment-body']}>
						{data.body && (
							<FuseHighlight
								matchedWords={
									data.$textHighlight?.matchedIndexes?.[
										'body'
									]
								}
								text={data?.body}
							/>
						)}
					</div>
					<HStack className={styles['comment-right-info']}>
						{data.hasFiles && (
							<OpenFileModal
								title={labels.documents}
								options={data.filesOptions}
								openFile={openFile}
							/>
						)}
						<CommentAuthor author={data.author} />
						<HStack
							className={styles['comment-id-replies']}
							align='center'
							gap='xsmall'
						>
							<VStack>
								<h5 className={styles['comment-id']}>
									#{data.id}
								</h5>
								<small>
									({data.repliesCount ?? 0}{' '}
									{data.repliesCount === 1
										? i18n`reply`
										: i18n`replies`}
									)
								</small>
							</VStack>
							{hasReplies && (
								<button
									className={`${styles['expand-toggle']} ${
										expanded ? styles['--expanded'] : ''
									}`}
									onClick={(e) => {
										e.stopPropagation()
										onToggleExpand && onToggleExpand()
									}}
								>
									<Icon
										width='18px'
										height='18px'
										name='unfold-more'
									/>
								</button>
							)}
						</HStack>
					</HStack>
					{data.isPinned && (
						<Icon
							name='pin-filled'
							height='18px'
							width='18px'
							className={styles['pinned']}
						/>
					)}
				</div>
				{expanded && replies && replies.length > 0 && (
					<div className={styles['inline-replies-container']}>
						{replies.map((reply) => (
							<InlineCommentReply key={reply.id} reply={reply} />
						))}
					</div>
				)}
			</div>
		)
	},
	(prevProps, nextProps) =>
		prevProps.data.id === nextProps.data.id &&
		prevProps.data.textHeight === nextProps.data.textHeight &&
		prevProps.data.$layout === nextProps.data.$layout &&
		prevProps.data.$detailLevel === nextProps.data.$detailLevel &&
		prevProps.data.$textHighlight?.query ===
			nextProps.data.$textHighlight?.query &&
		prevProps.expanded === nextProps.expanded &&
		prevProps.replies === nextProps.replies
)

CommentThreadCard.displayName = 'CommentThreadCard'
CommentThreadCard.defaultHeight = 68
CommentThreadCard.defaultSummaryHeight = 68
CommentThreadCard.defaultLines = 2

export default CommentThreadCard

export const CommentThreadCardSkeleton = (props) => {
	const { className, ...attributes } = props

	return (
		<div
			{...attributes}
			className={`card ${styles['root'] || ''} ${className || ''} ${
				styles['skeleton-card']
			}`}
		>
			<span className={styles['status-circle']} />
			<span className={`${styles['table']}`}>
				<div className={styles['thread-header']}>
					<h5>
						<Skeleton
							className={`${styles['skeleton']} ${styles['medium']}`}
						/>
					</h5>
				</div>
				<div
					className={`${styles['skeleton-thread']} ${styles['thread-body']}`}
				>
					<small>
						<Skeleton className={`${styles['skeleton']}`} />
					</small>
					<small>
						<Skeleton className={`${styles['skeleton']}`} />
					</small>
					<small>
						<Skeleton
							className={`${styles['skeleton']} ${styles['small']}`}
						/>
					</small>
				</div>
			</span>
		</div>
	)
}

CommentThreadCardSkeleton.defaultHeight = 60
CommentThreadCardSkeleton.verticalDefaultHeight = 60
